/**
 * JobsiteHomepageContainerBase - Base component for embedding JobsiteHomepage (JHP) in Zoomin
 *
 * This component contains the core logic for loading the federated JHP module
 * and transforming Zoomin's data structures to JHP's expected format.
 * Platform-specific wrappers (desktop/mobile) should use this component
 * and add their respective navigation and layout components.
 */

import * as React from 'react'

import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { UploadingType } from '~/client/graph'
import { Loader } from '~/client/src/shared/components/Loader'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import JobsiteHomepageConfigurationStore from '~/client/src/shared/stores/domain/JobsiteHomepageConfiguration.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import OffloadingEquipmentsStore from '~/client/src/shared/stores/domain/OffloadingEquipments.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import type TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WeatherForecastsStore from '~/client/src/shared/stores/domain/WeatherForecasts.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { JobsiteHomepageAdapter } from '../../adapters/JobsiteHomepageAdapter'
import {
  GET_PROJECT_MEMBERS,
  GET_WEATHER_FORECASTS,
  LOAD_AND_LISTEN_TO_COMPANIES,
  LOAD_AND_LISTEN_TO_GLOBE_VIEWS,
  LOAD_AND_LISTEN_TO_PROJECT_ADDRESS,
  LOAD_AND_LISTEN_TO_PROJECT_ROLES,
  LOAD_AND_LISTEN_TO_REMOTE_TILESETS,
  LOAD_AND_LISTEN_TO_SITEMAPS,
  LOAD_AND_LISTEN_TO_SITEMAP_ITEMS,
  LOAD_AND_LISTEN_TO_TILESETS,
  LOAD_AND_LISTEN_TO_USER_PROJECTS,
  LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES,
  LOAD_AND_LISTEN_TO_WORKFLOW_TYPES,
  LOAD_JOBSITE_HOMEPAGE_CONFIGURATION,
} from '../../stores/EventStore/eventConstants'
import JobsiteHomepageWorkflowsProvider from './JobsiteHomepageWorkflowsProvider'

import './JobsiteHomepageContainer.scss'

/** Max time to wait for required events before forcing JHP mount */
const JHP_FORCE_MOUNT_TIMEOUT_MS = 5000

/**
 * Events that must complete before JHP can be first mounted, so it receives a complete board
 * rather than one that fills in underneath it.
 *
 * This list no longer carries subscriptions: the snapshot is a computed read from this
 * component's own render reaction, so it tracks whatever the adapter reads (STX1-1009). Adding
 * an event here only widens the initial gate.
 */
const JHP_REQUIRED_EVENTS = [
  // Core configuration
  LOAD_JOBSITE_HOMEPAGE_CONFIGURATION,
  // Project info (header)
  LOAD_AND_LISTEN_TO_PROJECT_ADDRESS,
  // Map data
  LOAD_AND_LISTEN_TO_GLOBE_VIEWS,
  LOAD_AND_LISTEN_TO_SITEMAPS,
  LOAD_AND_LISTEN_TO_SITEMAP_ITEMS,
  LOAD_AND_LISTEN_TO_TILESETS,
  LOAD_AND_LISTEN_TO_REMOTE_TILESETS,
  // Contacts data
  GET_PROJECT_MEMBERS,
  LOAD_AND_LISTEN_TO_COMPANIES,
  LOAD_AND_LISTEN_TO_USER_PROJECTS,
  LOAD_AND_LISTEN_TO_PROJECT_ROLES,
  // Workflows data
  LOAD_AND_LISTEN_TO_WORKFLOW_TYPES,
  LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES,
  // Weather data
  GET_WEATHER_FORECASTS,
]

// Lazy load the federated JHP module
// @ts-ignore - TypeScript doesn't fully support Module Federation dynamic imports
const EmbeddedJHP = React.lazy(
  async (): Promise<{ default: React.ComponentType<any> }> => {
    try {
      const module = await import('jhp/EmbeddedApp')

      let Component: React.ComponentType<any> | undefined

      // Primary: default export (JHP uses `export default EmbeddedJHP`)
      if (module.default && typeof module.default === 'function') {
        Component = module.default as React.ComponentType<any>
      }
      // Fallback: named export (JHP also exports `export const EmbeddedJHP`)
      else if ((module as any).EmbeddedJHP) {
        Component = (module as any).EmbeddedJHP as React.ComponentType<any>
      }

      // Validate component
      if (
        !Component ||
        (typeof Component !== 'function' && typeof Component !== 'object')
      ) {
        throw new Error(
          `JHP module did not export a valid React component. Keys: ${Object.keys(
            module,
          ).join(', ')}`,
        )
      }

      return { default: Component }
    } catch (error) {
      console.error('Failed to load JHP module:', error)
      throw error
    }
  },
)

/**
 * Navigation targets for JHP header edit buttons
 */
type JHPNavigationTarget =
  | 'project-details'
  | 'project-time'
  | 'company-directory'

interface IJHPCallbacks {
  onSaveBoard: (config: any, settings?: any) => Promise<void>
  onNavigate?: (target: JHPNavigationTarget) => void
  onWorkflowClick?: (workflowId: string) => void
  onUploadFile: (file: File, type: 'image' | 'pdf') => Promise<{ url: string }>
}

/**
 * Props injected via MobX
 */
interface IProps {
  state?: InitialState
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  workflowTypesStore?: WorkflowTypesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  locationAttributesStore?: LocationAttributesStore
  offloadingEquipmentsStore?: OffloadingEquipmentsStore
  projectRolesStore?: ProjectRolesStore
  closuresStore?: ClosuresStore
  projectDateStore?: ProjectDateStore
  jobsiteHomepageConfigurationStore?: JobsiteHomepageConfigurationStore
  fileUploadingStore?: FileUploadingStore
  weatherForecastsStore?: WeatherForecastsStore
  tagsStore?: TagsStore
  /**
   * Optional callback for navigation from JHP header edit icons
   * Only provided on desktop where navigation is supported
   */
  onNavigate?: (target: JHPNavigationTarget) => void
  /**
   * Optional callback when user clicks a workflow in JHP
   * Opens the workflow in a sidebar view
   */
  onWorkflowClick?: (workflowId: string) => void
}

/**
 * JobsiteHomepageContainerBase - Base component for embedding JHP
 * This component handles the core JHP loading and data transformation logic.
 * Platform-specific wrappers should use this and add navigation/layout.
 */
@inject(
  'state',
  'userProjectsStore',
  'projectMembersStore',
  'companiesStore',
  'workflowTypesStore',
  'workflowCategoriesStore',
  'locationAttributesStore',
  'offloadingEquipmentsStore',
  'projectRolesStore',
  'closuresStore',
  'projectDateStore',
  'jobsiteHomepageConfigurationStore',
  'fileUploadingStore',
  'weatherForecastsStore',
  'tagsStore',
)
@observer
export default class JobsiteHomepageContainerBase extends React.Component<IProps> {
  /** Timeout ID for forcing JHP mount after delay */
  private forceRenderTimeoutId: number | null = null
  /** Whether to bypass event-based gating (set after timeout or after first successful render) */
  private bypassEventGate: boolean = false
  /** Whether JHP has been mounted at least once (prevents unmount/remount race) */
  private jhpHasMounted: boolean = false
  // Async lifecycle methods resume after a network round trip, by which point the mobile route
  // slider may already have unmounted this.
  private isUnmounted: boolean = false
  // A new identity re-wires four effects in the remote, so this is rebuilt only when the two
  // props that can vary actually do.
  private cachedCallbacks: IJHPCallbacks | null = null
  private cachedOnNavigate: IProps['onNavigate'] = undefined
  private cachedOnWorkflowClick: IProps['onWorkflowClick'] = undefined
  private disposeProjectReaction: (() => void) | null = null
  // Bumped whenever a load is started for a new gate. A continuation resuming after its
  // generation has passed belongs to a project the container has already left.
  private gateGeneration: number = 0
  @observable.ref private workflows: Workflow[] = []
  @observable.ref private workflowsLoading: boolean = true
  // mobx-react's dev accessor throws on this.props from a foreign derivation, so the computeds
  // read this instead. Seeded once: every prop they touch is an injected singleton.
  @observable.ref private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  // By content, not identity: a source returning a fresh array per render would otherwise
  // re-render this component, re-render the source, and spin.
  @action.bound
  private handleWorkflowsChange(workflows: Workflow[], loading: boolean): void {
    const isSameList =
      this.workflows.length === workflows.length &&
      this.workflows.every((workflow, i) => workflow === workflows[i])

    if (!isSameList) {
      this.workflows = workflows
    }
    this.workflowsLoading = loading
  }

  @computed
  private get jhpUser(): any {
    const { state, userProjectsStore } = this.localProps

    return JobsiteHomepageAdapter.transformUser(state.user, userProjectsStore)
  }

  @computed
  private get jhpBoardData(): any {
    const {
      state,
      projectMembersStore,
      userProjectsStore,
      companiesStore,
      workflowTypesStore,
      workflowCategoriesStore,
      locationAttributesStore,
      offloadingEquipmentsStore,
      projectRolesStore,
      closuresStore,
      projectDateStore,
      weatherForecastsStore,
      tagsStore,
    } = this.localProps

    return JobsiteHomepageAdapter.transformBoardData(
      state.activeProject,
      state,
      {
        projectMembersStore,
        userProjectsStore,
        companiesStore,
        workflowTypesStore,
        workflowCategoriesStore,
        locationAttributesStore,
        offloadingEquipmentsStore,
        projectRolesStore,
        closuresStore,
        projectDateStore,
        weatherForecastsStore,
        tagsStore,
      },
      this.workflows,
    )
  }

  @computed
  private get parsedConfig(): any {
    return this.localProps.jobsiteHomepageConfigurationStore
      ?.parsedConfiguration
  }

  public async componentDidMount() {
    // Registered before the first await. `state` is an injected singleton, so the previous
    // prevProps comparison could never fire; capturing it here also keeps this.props out of the
    // derivation, where mobx-react's dev accessor throws.
    const { state } = this.props
    this.disposeProjectReaction = reaction(
      () => state?.activeProject?.id,
      () => this.reloadForProjectSwitch(),
    )

    const generation = ++this.gateGeneration
    await this.loadConfigurationIfNeeded()
    if (this.isUnmounted || generation !== this.gateGeneration) return

    // Trigger weather data fetch for JHP weather widget.
    // On desktop, CompactWeather.tsx in the NavBar triggers this, but mobile
    // doesn't have that component, so we need to trigger it here for both platforms.
    this.fetchWeatherData()

    this.armForceMountTimeout()
  }

  private async reloadForProjectSwitch(): Promise<void> {
    if (this.isUnmounted) return

    // Cancelled with the gate it belongs to. The store keeps the previous configuration while
    // the new one loads, so a timer left over from the last project would open this gate onto
    // the wrong board.
    this.clearForceMountTimeout()
    this.jhpHasMounted = false
    this.bypassEventGate = false

    const generation = ++this.gateGeneration
    await this.loadConfigurationIfNeeded()
    if (this.isUnmounted || generation !== this.gateGeneration) return

    this.armForceMountTimeout()
  }

  private clearForceMountTimeout(): void {
    if (this.forceRenderTimeoutId !== null) {
      window.clearTimeout(this.forceRenderTimeoutId)
      this.forceRenderTimeoutId = null
    }
  }

  private armForceMountTimeout(): void {
    // The gate may have opened while the configuration was loading; there is nothing left to
    // rescue, and arming anyway costs a forceUpdate five seconds later.
    if (this.jhpHasMounted || this.bypassEventGate) return

    this.clearForceMountTimeout()

    this.forceRenderTimeoutId = window.setTimeout(() => {
      if (this.isUnmounted) return

      const { state } = this.props
      if (!state?.loading) return

      const pendingEvents = JHP_REQUIRED_EVENTS.filter(event =>
        state.loading.get(event),
      )

      if (pendingEvents.length > 0) {
        console.warn(
          `[JHP Container] Forcing mount after ${JHP_FORCE_MOUNT_TIMEOUT_MS}ms timeout. Events still pending:`,
          pendingEvents,
        )
      }

      this.bypassEventGate = true
      this.forceUpdate()
    }, JHP_FORCE_MOUNT_TIMEOUT_MS)
  }

  public componentWillUnmount() {
    this.isUnmounted = true

    if (this.disposeProjectReaction) {
      this.disposeProjectReaction()
      this.disposeProjectReaction = null
    }

    this.clearForceMountTimeout()
  }

  /**
   * Fetch weather data for JHP weather widget.
   * This mirrors what CompactWeather.tsx does on desktop NavBar mount.
   */
  private fetchWeatherData() {
    const { weatherForecastsStore } = this.props
    if (!weatherForecastsStore) return

    const todayAtNoon = new Date()
    todayAtNoon.setHours(12, 0, 0, 0)
    weatherForecastsStore.fetch5DayForecast(todayAtNoon.getTime())
  }

  // The store dedupes per project and keeps whatever it already holds, so a remount no longer
  // blanks the board for the duration of a refetch.
  private async loadConfigurationIfNeeded() {
    const { state, jobsiteHomepageConfigurationStore } = this.props

    if (!state?.activeProject?.id || !jobsiteHomepageConfigurationStore) {
      return
    }

    try {
      await JobsiteHomepageAdapter.getWidgetConfiguration(
        jobsiteHomepageConfigurationStore,
      )
      // MobX observable `isLoaded` change will trigger re-render automatically
      // via the @observer decorator - no forceUpdate() needed
    } catch (error) {
      console.error('Failed to load widget configuration:', error)
    }
  }

  /** Handle board save from JHP (admin users only) */
  private readonly handleSaveBoard = async (
    config: any,
    settings?: any,
  ): Promise<void> => {
    const { state, jobsiteHomepageConfigurationStore } = this.props
    const projectId = state?.activeProject?.id
    const userId = state?.user?.id

    if (projectId && userId && jobsiteHomepageConfigurationStore) {
      try {
        await JobsiteHomepageAdapter.saveWidgetConfiguration(
          projectId,
          userId,
          config,
          jobsiteHomepageConfigurationStore,
          settings,
        )
      } catch (error) {
        console.error('Failed to save board configuration:', error)
      }
    }
  }

  /** Handle file upload from JHP rich text editor */
  private readonly handleUploadFile = async (
    file: File,
    type: 'image' | 'pdf',
  ): Promise<{ url: string }> => {
    const { state, fileUploadingStore } = this.props
    const projectId = state?.activeProject?.id

    if (!fileUploadingStore || !projectId) {
      throw new Error('File upload not available')
    }

    const uploadingType =
      type === 'image' ? UploadingType.Image : UploadingType.Pdf

    const result = await fileUploadingStore.uploadFile(
      file,
      uploadingType,
      projectId,
      file.name,
      true, // omitLogCreation - don't create upload log entries for JHP content
    )

    const fileUrl = result?.[0]?.fileURL
    if (!fileUrl) {
      throw new Error('File upload failed - no URL returned')
    }

    return { url: fileUrl }
  }

  /** Maps Zoomin language names to ISO locale codes */
  private getISOLocale(): string {
    const localeMap: Record<string, string> = {
      English: 'en',
      Español: 'es',
      Français: 'fr',
      Українська: 'uk',
    }
    return localeMap[Localization.state.currentLanguage] || 'en'
  }

  private getCallbacks(): IJHPCallbacks {
    const { onNavigate, onWorkflowClick } = this.props

    if (
      this.cachedCallbacks &&
      this.cachedOnNavigate === onNavigate &&
      this.cachedOnWorkflowClick === onWorkflowClick
    ) {
      return this.cachedCallbacks
    }

    const callbacks: IJHPCallbacks = {
      onSaveBoard: this.handleSaveBoard,
      onUploadFile: this.handleUploadFile,
    }

    // Conditional: the remote branches on key presence, not value.
    if (onNavigate) {
      callbacks.onNavigate = onNavigate
    }

    if (onWorkflowClick) {
      callbacks.onWorkflowClick = onWorkflowClick
    }

    this.cachedCallbacks = callbacks
    this.cachedOnNavigate = onNavigate
    this.cachedOnWorkflowClick = onWorkflowClick

    return callbacks
  }

  private renderJHPContent = (locale: string): JSX.Element => {
    const { state, jobsiteHomepageConfigurationStore } = this.props

    // CRITICAL GATE: Check if all required data has loaded before mounting JHP.
    // JHP needs complete data on first mount - it doesn't handle prop updates well.
    // By waiting for all required events to complete, we ensure JHP receives
    // all the data it needs in one go, avoiding timing/race condition issues.
    //
    // RACE CONDITION FIX: Once JHP has mounted successfully, we must NOT unmount it
    // again due to event gating. This prevents a race condition where:
    // 1. JHP mounts (gates pass)
    // 2. useJHPStyles enables CSS
    // 3. On next render, MobX triggers re-render with pending events (briefly)
    // 4. Event gate fails, JHP unmounts
    // 5. useJHPStyles cleanup disables CSS
    // 6. Events complete, JHP remounts
    // 7. But CSS is broken due to the intermediate unmount/re-enable cycle
    //
    // By checking jhpHasMounted, we keep JHP mounted once it's been shown.
    if (!this.bypassEventGate && !this.jhpHasMounted) {
      const pendingEvents = JHP_REQUIRED_EVENTS.filter(event =>
        state.loading.get(event),
      )

      if (pendingEvents.length > 0) {
        // Still waiting for required data - show loader
        return <Loader hint={Localization.translator.loading} />
      }

      // Also ensure the configuration store has processed the loaded data
      const isConfigurationLoaded = jobsiteHomepageConfigurationStore?.isLoaded
      if (!isConfigurationLoaded) {
        return <Loader hint={Localization.translator.loading} />
      }
    }

    // JHP is about to mount - clear the timeout since we no longer need it
    this.clearForceMountTimeout()

    // Mark that JHP has been mounted at least once - this prevents future
    // unmount/remount cycles due to transient event loading states
    if (!this.jhpHasMounted) {
      this.jhpHasMounted = true
    }

    // Key forces JHP remount on project switch
    const projectKey = state?.activeProject?.id || 'no-project'

    // Derived, so the snapshot keeps its identity: a fresh one re-runs every effect in the
    // remote and re-hydrates its map.
    const { jhpUser, jhpBoardData, parsedConfig } = this
    const widgetSetup = parsedConfig?.widgets
    const boardSettings = parsedConfig?.settings

    // All required data is loaded at this point (gated above).
    // isLoading only reflects workflow loading now - workflows are fetched
    // via GraphQL in JobsiteHomepageWorkflowsProvider, not via event system.
    const isLoading = this.workflowsLoading

    const callbacks = this.getCallbacks()

    return (
      <React.Suspense
        key={projectKey}
        fallback={<Loader hint={Localization.translator.loading} />}
      >
        <EmbeddedJHP
          user={jhpUser}
          boardData={jhpBoardData}
          widgetSetup={widgetSetup}
          boardSettings={boardSettings}
          isLoading={isLoading}
          locale={locale}
          callbacks={callbacks}
        />
      </React.Suspense>
    )
  }

  public render(): JSX.Element {
    const { state } = this.props

    if (!state?.user || !state?.activeProject) {
      return <Loader hint={Localization.translator.loading} />
    }

    // Access locale in render() so MobX tracks language changes
    const locale = this.getISOLocale()

    // The snapshot is built in this render reaction, so everything the adapter reads is
    // subscribed natively and the hand-rolled tracking reads are gone.
    return (
      <div className="jobsite-homepage-container">
        <JobsiteHomepageWorkflowsProvider
          projectId={state.activeProject.id}
          userId={state.user.id}
          onChange={this.handleWorkflowsChange}
        />
        {this.renderJHPContent(locale)}
      </div>
    )
  }
}
